import { Point, Polygon } from 'content-creation-common/models';

import * as KeyFrameSelectors from './keyframe';
import * as ElementSelectors from './element';
import * as UISelectors from './ui';
import { isNotNil } from '../util/object';
import { isNumber } from '../util/numbers';
import { CONTENT_TYPE } from '../constants';
import {
  OVERFLOW_X,
  OVERFLOW_Y,
  VERTICAL_TEXT_ALIGNMENT,
} from '../features/text/constants';
import { getCurrentFrame } from '../features/playback/manager/PlaybackManager';

export const isElementUnderPoint = (state, elementId, point) => {
  const currentFrame = getCurrentFrame();
  const element = ElementSelectors.getById(state, elementId);
  const isGroup = ElementSelectors.isGroup(state, elementId);
  const templateExclusion = ElementSelectors.getMetaTemplateExclusion(
    state,
    elementId,
  );
  const editTemplate = UISelectors.editTemplate(state);
  const isVisible = ElementSelectors.isVisible(state, elementId);
  const content = ElementSelectors.getContent(state, elementId);

  if (
    element.selectable /* element is selecteable */ &&
    isVisible /* content is visible */ &&
    !isGroup /* element is not a group */ &&
    (editTemplate ||
      !templateExclusion) /* user is editing a template or this element hasn't been excluded by a template */ &&
    currentFrame >= element.frameStart &&
    currentFrame <= element.frameEnd /* this element is in frame */
  ) {
    const media = ElementSelectors.getMedia(state, elementId);
    const transform = KeyFrameSelectors.getElementTransformWithParentTransformsAtFrame(
      state,
      elementId,
      currentFrame,
    );
    const {
      scale: { x: scaleX, y: scaleY },
      translation: { x, y },
      rotation,
      alpha,
    } = transform;

    if (alpha <= 0) {
      return false;
    }

    let center = new Point(x, y);

    let { width, height } = media;

    if (
      isNotNil(content) &&
      content.contentType === CONTENT_TYPE.TEXT &&
      content.overflowY === OVERFLOW_Y.VISIBLE &&
      content.overflowX === OVERFLOW_X.WRAP
    ) {
      if (isNumber(content.naturalWidth) && width < content.naturalWidth) {
        width = content.naturalWidth;
      }

      if (isNumber(content.naturalHeight) && height < content.naturalHeight) {
        if (content.verticalAlignment === VERTICAL_TEXT_ALIGNMENT.TOP) {
          center = new Point(
            x,
            y + 0.5 * (content.naturalHeight - height) * scaleY,
          );
        } else if (
          content.verticalAlignment === VERTICAL_TEXT_ALIGNMENT.BOTTOM
        ) {
          center = new Point(
            x,
            y - 0.5 * (content.naturalHeight - height) * scaleY,
          );
        }

        height = content.naturalHeight;
      }
    }

    const topLeft = new Point(
      center.x - (width * scaleX) / 2,
      center.y - (height * scaleY) / 2,
    );

    const bottomLeft = new Point(topLeft.x, topLeft.y + height * scaleY);

    const topRight = new Point(topLeft.x + width * scaleX, topLeft.y);

    const bottomRight = new Point(topRight.x, bottomLeft.y);

    topLeft.rotateAroundPoint(center, rotation);
    topRight.rotateAroundPoint(center, rotation);
    bottomRight.rotateAroundPoint(center, rotation);
    bottomLeft.rotateAroundPoint(center, rotation);

    const minX = Math.min(topLeft.x, topRight.x, bottomRight.x, bottomLeft.x);
    const maxX = Math.max(topLeft.x, topRight.x, bottomRight.x, bottomLeft.x);

    const minY = Math.min(topLeft.y, topRight.y, bottomRight.y, bottomLeft.y);
    const maxY = Math.max(topLeft.y, topRight.y, bottomRight.y, bottomLeft.y);

    if (
      point.x >= minX &&
      point.x <= maxX &&
      point.y >= minY &&
      point.y <= maxY
    ) {
      const polygon = new Polygon([topLeft, topRight, bottomRight, bottomLeft]);
      return polygon.hasPointInside(point);
    }
  }

  return false;
};

export const getElementFromPoint = (state, point) => {
  const elements = ElementSelectors.getAllIds(state);
  const reverseElements = [...elements].reverse();
  return reverseElements.find((elementId) => {
    return isElementUnderPoint(state, elementId, point);
  });
};
